/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { Dataset, DeltaDataset, FilePath, LakehouseLinkedService, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DeltaSettings } from 'app/model/dataflow/dataset-settings/delta-dataset-settings';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { ensureAllPathsAreUnique, getAdlsDatasetForPreview, initializeCommonConnectionProperties, processEachADLSEntity, processEachDeltaLakehouseEntity } from 'app/components/create-datamapper/utils/adls-component-utils';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';
import { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { Subject } from 'rxjs';
import { LoggerService } from 'app/logger/logger.service';

export class DeltaDatasetTypeHandler extends DatasetTypeHandler {

    constructor(
        public isSource: Boolean
    ) {
        super();
    }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('DeltaSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as DeltaSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as DeltaSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.container.expression = !incomingSourceDatasetsSettings.container ? undefined : incomingSourceDatasetsSettings.container.expression;
        outputSourceDatasetsSettings.folderPath.expression = !incomingSourceDatasetsSettings.folderPath ? undefined : incomingSourceDatasetsSettings.folderPath.expression;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length > 0) {
            disableButton = false;
            selectedRows.forEach((elem) => {
                if (elem.entityName === '' || !elem.dataset) {
                    disableButton = true;
                }
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !ensureAllPathsAreUnique(selectedRows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [disableButton, disableButtonStatus];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedTargetLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        if (selectedTargetLinkedService instanceof LakehouseLinkedService) {
            selectedRows.forEach((elem) => {
                processEachDeltaLakehouseEntity(selectedTargetLinkedService, elem, selectedDatasetFormat, tables, this.isSource);
            });
        } else {
            selectedRows.forEach((elem) => {
                let filePath = new FilePath();
                filePath = (elem.dataset as DeltaDataset).location.getLocation();
                if (!filePath.container) {
                    const pathArray = elem.entityName.split('/');
                    filePath.container = pathArray[0];
                    filePath.folderPath = pathArray.slice(1, pathArray.length).join('/');
                }
                processEachADLSEntity(filePath, selectedTargetLinkedService, elem, selectedDatasetFormat, tables, this.isSource);
            });
        }

        return tables;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);

        // Create and populate DeltaSettings
        const deltaSettings = Owned.create<DeltaSettings>(DeltaSettings.MODEL, table as DataFlowSource);
        deltaSettings.initialize(undefined);
        deltaSettings.container = createDataflowExpression(container) as DataFlowExpression;
        deltaSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;

        return deltaSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        return getAdlsDatasetForPreview(DatasetFormatType.Delta, container, folderPath, linkedService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        initializeCommonConnectionProperties(mapperConnectionInfo.connection, selectedDatasetFormat, this.isSource);
    }

    public async fetchEntities(loggerService: LoggerService, queryApiFactoryService: QueryApiFactoryService, linkedService: LinkedService,
        isSource: boolean, _selectedDatasetFormat?: DatasetFormatType, isDebugSessionActive: () => Promise<boolean> = undefined,
        getEntitiesForADataFlowSource: (source: DataFlowSource, resultSubject: Subject<IMapperEntity[]>) => Promise<void> = undefined): Promise<any[]> {
        if (linkedService instanceof LakehouseLinkedService) {
            linkedService.cachedResource.properties.typeProperties.rootFolder = 'Tables';
            return await queryApiFactoryService.getWizardQueryApi().getNavTableAsync(linkedService);
        }
        return [''];
    }

}
